import React, {
  useCallback, useContext, useState
} from 'react';
import { useNavigate } from 'react-router-dom';

import { ButtonBar, ScreenHeader } from '../../components';
import { useScrollToTop } from '../../hooks';

import { NewPOAContext } from './new-poa-composer';

export function NewPOAPayingAgent() {
  const { powerOfAttorney, setPowerOfAttorney } = useContext(NewPOAContext);
  const navigate = useNavigate();
  const [ payAgent, setPayAgent ] = useState(powerOfAttorney.pay_agent || 'no');
  const [ specifiedAmount, setSpecifiedAmount ] = useState(powerOfAttorney.specified_agent_payment || '');
  
  useScrollToTop();

  const handleBack = useCallback(() => {
    navigate('../keeping-assets-separate');
  }, [ navigate ]);

  const handleNext = useCallback(() => {
    setPowerOfAttorney(prev => ({ 
      ...prev, 
      pay_agent: payAgent,
      specified_agent_payment: payAgent === 'specified' ? specifiedAmount : ''
    }));
    navigate('../summary');
  }, [ payAgent, specifiedAmount, setPowerOfAttorney, navigate ]);
  
  return (
    <div className="new-poa-paying-agent screen-container screen">
      <ScreenHeader
        title="Paying Your Agent"
        description="Decide whether and how much to compensate your agent"
        handleBack={handleBack}
      />
      
      <div className="content-section">
        <h3>Should Your Agent Be Compensated?</h3>
        <p>
          Acting as a financial agent can involve significant time and responsibility. 
          You can choose whether to compensate your agent for their services.
        </p>

        <h3>Compensation Considerations</h3>
        <ul className="payment-considerations">
          <li>Time and effort required to manage your affairs</li>
          <li>Complexity of your financial situation</li>
          <li>Professional expertise needed</li>
          <li>Relationship with your agent</li>
          <li>Local customs and practices</li>
        </ul>

        <h3>How Do You Want to Pay Your Agent?</h3>
        <div className="payment-options">
          <div className="payment-option">
            <label className="payment-label">
              <input
                type="radio"
                name="pay-agent"
                value="no"
                checked={payAgent === 'no'}
                onChange={(e) => setPayAgent(e.target.value)}
              />
              <div className="payment-content">
                <h4>No Payment</h4>
                <p>My agent will serve without compensation</p>
              </div>
            </label>
          </div>

          <div className="payment-option">
            <label className="payment-label">
              <input
                type="radio"
                name="pay-agent"
                value="reasonable"
                checked={payAgent === 'reasonable'}
                onChange={(e) => setPayAgent(e.target.value)}
              />
              <div className="payment-content">
                <h4>Reasonable Amount (Determined by Agent)</h4>
                <p>My agent may take reasonable compensation for their services</p>
              </div>
            </label>
          </div>

          <div className="payment-option">
            <label className="payment-label">
              <input
                type="radio"
                name="pay-agent"
                value="specified"
                checked={payAgent === 'specified'}
                onChange={(e) => setPayAgent(e.target.value)}
              />
              <div className="payment-content">
                <h4>Specific Amount I Specify</h4>
                <p>My agent will receive the amount I determine</p>
              </div>
            </label>
          </div>
        </div>

        {payAgent === 'specified' && (
          <div className="specified-amount-section">
            <h3>Specify Payment Amount</h3>
            <div className="amount-input-group">
              <label htmlFor="specified-amount" className="amount-label">
                Describe the payment arrangement
              </label>
              <textarea
                id="specified-amount"
                value={specifiedAmount}
                onChange={(e) => setSpecifiedAmount(e.target.value)}
                className="amount-textarea"
                placeholder="e.g., $500 per month, 5% of assets under management annually, $50 per hour for time spent"
                rows={3}
              />
              <p className="amount-help">
                Be specific about the amount, frequency, and any conditions for payment.
              </p>
            </div>
          </div>
        )}

        <div className="important-note">
          <h4>Important:</h4>
          <p>
            Agent compensation is paid from your assets. If you choose "reasonable amount," 
            your agent should consider factors like time spent, complexity of tasks, 
            and local standards for similar services.
          </p>
        </div>
      </div>

      <ButtonBar 
        handlePrimary={handleNext}
        primaryText="Continue"
      />
    </div>
  );
}
